import type { AuthProviderConfig } from '@auth/domain';
import { generateCodeVerifier, generateCodeChallenge, storeCodeVerifier, storeProviderId, generateAndStoreState } from '@auth/utils';
import { track } from '@core/logging/appInsightsIntegration';
import { useState, useCallback } from 'react';

/**
 * Configuration for building an authorization URL
 */
interface AuthUrlConfig {
  provider: AuthProviderConfig;
  redirectUri: string;
  codeChallenge: string;
  state: string;
}

/**
 * Build authorization URL with PKCE parameters
 */
function buildAuthUrl(config: AuthUrlConfig): string {
  const { provider, redirectUri, codeChallenge, state } = config;
  const authUrl = new URL(provider.authServerUrl);

  // Add required parameters
  authUrl.searchParams.append('response_type', provider.responseType || 'code');
  authUrl.searchParams.append('client_id', provider.clientId);
  authUrl.searchParams.append('redirect_uri', redirectUri);
  authUrl.searchParams.append('code_challenge', codeChallenge);
  authUrl.searchParams.append('code_challenge_method', 'S256');
  authUrl.searchParams.append('state', state);
  authUrl.searchParams.append('scope', provider.scopes.join(' '));

  return authUrl.toString();
}

/**
 * Hook to handle login initiation with loading and error states
 */
export function useLogin() {
  const [error, setError] = useState<string | null>(null);

  const initiateLogin = useCallback(async (provider: AuthProviderConfig, redirectUri: string, postAuthRedirectUrl?: string): Promise<void> => {
    try {
      setError(null);

      track('LoginInitiated', {
        providerId: provider.id,
        hasPostAuthRedirect: postAuthRedirectUrl ? 'true' : 'false',
      });

      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateAndStoreState(postAuthRedirectUrl);

      // Store PKCE parameters in session storage
      storeCodeVerifier(codeVerifier);
      storeProviderId(provider.id);

      // Build and redirect to authorization URL
      const authUrl = buildAuthUrl({
        provider,
        redirectUri,
        codeChallenge,
        state,
      });

      track('LoginRedirect', {
        providerId: provider.id,
        authUrl: authUrl.substring(0, 100), // Truncated for privacy
      });

      // Redirect to auth provider
      window.location.href = authUrl;
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));

      track('LoginFailed', {
        providerId: provider.id,
        errorType: error instanceof Error ? error.name : 'unknown',
        errorMessage: error instanceof Error ? error.message.substring(0, 100) : 'unknown',
      });
    }
  }, []);

  return {
    initiateLogin,
    error,
  };
}
